import React from 'react';
import { HeartHandshake, Maximize2, Shield, ShieldCheck, ShieldHalf, Sun } from 'lucide-react';
import { hasPaladinFeature, getPaladinLevel } from '../../../shared/rules/paladin.mjs';
import { hasSubclassFeature } from '../../../shared/rules/subclasses/index.mjs';
import { incapacitationReason } from '../../../shared/rules/barbarianRage.mjs';
import { AURA_OF_PROTECTION, auraOfProtectionRadius } from '../../../shared/rules/effects/auras.mjs';
import { notify } from '../../../utils/notification';

/**
 * Reminder tiles for the Paladin's always-on features (2024). Nothing is spent: a tile is lit while its feature is
 * working and dim otherwise (the auras go out while the Paladin is Incapacitated or at 0 Hit Points; Smite of
 * Protection lights after Divine Smite until the start of the Paladin's next turn). Clicking one explains it.
 */
export const paladinPassiveTiles = (form, characterId, combatState) => {
  const selfId = String(characterId);
  const own = (combatState?.activeEffects || []).filter(effect => effect?.definitionId === AURA_OF_PROTECTION && String(effect.sourceCombatantId) === selfId);
  const onSelf = own.find(effect => String(effect.targetCombatantId) === selfId);
  const blocked = incapacitationReason({ ...form, combatantId: selfId }, combatState);
  const auraOn = !blocked;
  const radius = onSelf?.radiusFeet || auraOfProtectionRadius(getPaladinLevel(form));
  const off = blocked ? `Off while ${blocked}.` : '';
  const tiles = [
    hasPaladinFeature(form, 'auraOfProtection') && { id: 'aura-of-protection', name: 'Aura of Protection', Icon: Shield, accent: '#f6d365', lit: auraOn,
      value: auraOn ? (onSelf?.bonus ? `+${onSelf.bonus} saves` : `${radius} ft`) : 'Off',
      about: `You and your allies within ${radius} feet add your Charisma modifier (minimum +1) to saving throws.` },
    hasPaladinFeature(form, 'auraOfCourage') && { id: 'aura-of-courage', name: 'Aura of Courage', Icon: ShieldCheck, accent: '#ffb86b', lit: auraOn,
      value: auraOn ? 'No Frightened' : 'Off', about: 'You and your allies in your Aura of Protection have Immunity to the Frightened condition.' },
    hasSubclassFeature(form, 'paladin:devotion:aura-of-devotion') && { id: 'aura-of-devotion', name: 'Aura of Devotion', Icon: HeartHandshake, accent: '#ff9ecf', lit: auraOn,
      value: auraOn ? 'No Charmed' : 'Off', about: 'You and your allies in your Aura of Protection have Immunity to the Charmed condition.' },
    hasPaladinFeature(form, 'auraExpansion') && { id: 'aura-expansion', name: 'Aura Expansion', Icon: Maximize2, accent: '#9fe6ff', lit: auraOn,
      value: auraOn ? '30 ft' : 'Off', about: 'Your Aura of Protection is a 30-foot Emanation.' },
    hasPaladinFeature(form, 'radiantStrikes') && { id: 'radiant-strikes', name: 'Radiant Strikes', Icon: Sun, accent: '#ffe08a', lit: !blocked,
      value: blocked ? 'Off' : '+1d8 Radiant', about: 'Your hits with a Melee weapon or an Unarmed Strike deal an extra 1d8 Radiant damage (added automatically).' },
    hasSubclassFeature(form, 'paladin:devotion:smite-of-protection') && (() => {
      const lit = own.some(effect => effect.halfCover === true);
      return { id: 'smite-of-protection', name: 'Smite of Protection', Icon: ShieldHalf, accent: '#c9b6ff', lit, value: lit ? 'Half Cover' : 'After Smite',
        about: 'After you cast Divine Smite, you and your allies in your Aura of Protection have Half Cover until the start of your next turn.' };
    })(),
  ].filter(Boolean);
  // A dim aura says why it is off.
  return tiles.map(tile => ({ ...tile, about: !tile.lit && off && tile.id !== 'smite-of-protection' ? `${tile.about} ${off}` : tile.about }));
};

export default function PaladinPassiveTiles({ form, characterId, combatState }) {
  const tiles = paladinPassiveTiles(form, characterId, combatState);
  if (!tiles.length) return null;
  return <>{tiles.map(({ id, name, Icon, accent, lit, value, about }) => (
    <button key={id} type="button" data-action-tile={`paladin:${id}`} aria-label={`${name}: ${lit ? 'active' : 'inactive'}`} title={about}
      className={`combat-hud-resource-tile combat-hud-feature-action-tile combat-hud-resource-tile--passive${lit ? '' : ' combat-hud-resource-tile--dormant'}`}
      style={{ '--hud-resource-accent': accent }} onClick={() => notify(`${name}: ${about}`, 'info')}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Icon size={20}/></span>
      <span className="combat-hud-resource-tile__name">{name}</span>
      <span className="combat-hud-resource-tile__value">{value}</span>
    </button>
  ))}</>;
}
